import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Download, Linkedin, Mail, MapPin, Check } from "lucide-react";
import { FadeUp } from "@/components/motion/Reveal";
import { PROFILE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const initial = { name: "", email: "", subject: "", message: "", company_website: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!form.message.trim()) next.message = "Please enter a message.";
    return next;
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 20000 });
      setSubmitted(true);
      setForm(initial); // clear only on success
      toast.success("Message sent — thank you!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Your message was kept — please try again.");
      // form data is preserved (we do not reset on failure)
    } finally {
      setLoading(false);
    }
  };

  const labelCls = "font-mono text-[12px] uppercase tracking-[0.18em] text-chalk/70";
  const inputCls =
    "w-full rounded-none border-0 border-b border-line bg-transparent pb-3 font-display text-xl text-chalk placeholder:text-ash/60 focus:border-signal focus:outline-none focus:ring-0";
  const errCls = "mt-2 font-mono text-[12px] tracking-wide text-signal";

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-line bg-ink">
      <div className="mx-auto max-w-[1600px] scroll-mt-24 px-6 py-24 md:px-12 md:py-36">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <p className="mb-4 font-mono text-[12px] uppercase tracking-[0.2em] text-signal">/ Contact</p>
            <h2 className="font-display text-5xl font-semibold uppercase leading-[0.85] tracking-tightest text-chalk sm:text-6xl md:text-7xl">
              Let&apos;s make
              <br />
              <span className="text-outline-signal">it happen.</span>
            </h2>
            <p className="mt-8 max-w-sm text-[15px] leading-[1.65] text-ash md:text-base">
              I&apos;m currently open to full-time remote opportunities, contract work and selected freelance
              projects in product design, UI/UX, WordPress, branding and creative technology.
            </p>

            <a
              href={PROFILE.resumeUrl}
              download
              data-testid="resume-download"
              className="group mt-8 inline-flex min-h-[52px] items-center gap-3 rounded-none border border-signal px-6 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-signal transition-colors duration-300 hover:bg-signal hover:text-ink"
            >
              <Download className="h-4 w-4" />
              Download Résumé
            </a>

            <ul className="mt-10 space-y-4 border-t border-line pt-8">
              <li>
                <a href={`mailto:${PROFILE.email}`} data-testid="contact-email-link" className="group inline-flex items-center gap-3 font-mono text-[13px] uppercase tracking-[0.12em] text-chalk transition-colors hover:text-signal">
                  <Mail className="h-4 w-4 text-signal" />
                  {PROFILE.email}
                </a>
              </li>
              <li>
                <a href={PROFILE.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" className="group inline-flex items-center gap-3 font-mono text-[13px] uppercase tracking-[0.12em] text-chalk transition-colors hover:text-signal">
                  <Linkedin className="h-4 w-4 text-signal" />
                  LinkedIn
                </a>
              </li>
              <li className="inline-flex items-center gap-3 font-mono text-[13px] uppercase tracking-[0.12em] text-ash">
                <MapPin className="h-4 w-4 text-signal" />
                {PROFILE.location}
              </li>
              <li className="inline-flex items-center gap-3 font-mono text-[13px] uppercase tracking-[0.12em] text-ash">
                <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-signal" />
                Available for remote work
              </li>
            </ul>
          </FadeUp>

          <FadeUp delay={0.1} className="lg:col-span-7 lg:pl-8">
            {submitted ? (
              <div data-testid="contact-success" className="flex h-full flex-col items-start justify-center border border-signal/40 bg-surface p-10">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-signal">
                  <Check className="h-6 w-6 text-signal" />
                </span>
                <h3 className="mt-6 font-display text-3xl font-medium uppercase tracking-tight text-chalk">Message received</h3>
                <p className="mt-3 max-w-md text-[15px] leading-[1.6] text-ash">
                  Thanks for reaching out — your message has landed in my inbox and I&apos;ll reply to you directly. Talk soon.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSubmitted(false)}
                  className="mt-8 inline-flex min-h-[48px] items-center gap-2 border border-line px-6 py-3 font-mono text-[12px] uppercase tracking-[0.2em] text-chalk transition-colors hover:border-signal hover:text-signal"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" noValidate className="flex flex-col gap-9">
                {/* Honeypot — hidden from users, catches bots */}
                <input
                  type="text"
                  name="company_website"
                  value={form.company_website}
                  onChange={update}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 opacity-0"
                />

                <div className="grid grid-cols-1 gap-9 md:grid-cols-2">
                  <div className="flex flex-col gap-3">
                    <label htmlFor="name" className={labelCls}>Your Name <span className="text-signal">*</span></label>
                    <input id="name" name="name" data-testid="contact-name" value={form.name} onChange={update}
                      placeholder="Jane Doe" required aria-required="true"
                      aria-invalid={!!errors.name} aria-describedby={errors.name ? "err-name" : undefined}
                      className={inputCls} />
                    {errors.name && <p id="err-name" className={errCls}>{errors.name}</p>}
                  </div>
                  <div className="flex flex-col gap-3">
                    <label htmlFor="email" className={labelCls}>Email <span className="text-signal">*</span></label>
                    <input id="email" name="email" type="email" data-testid="contact-email" value={form.email} onChange={update}
                      placeholder="jane@studio.com" required aria-required="true"
                      aria-invalid={!!errors.email} aria-describedby={errors.email ? "err-email" : undefined}
                      className={inputCls} />
                    {errors.email && <p id="err-email" className={errCls}>{errors.email}</p>}
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <label htmlFor="subject" className={labelCls}>Subject (optional)</label>
                  <input id="subject" name="subject" data-testid="contact-subject" value={form.subject} onChange={update}
                    placeholder="Role, project, collaboration…" className={inputCls} />
                </div>

                <div className="flex flex-col gap-3">
                  <label htmlFor="message" className={labelCls}>Message <span className="text-signal">*</span></label>
                  <textarea id="message" name="message" data-testid="contact-message" rows={4} value={form.message} onChange={update}
                    placeholder="Tell me about the role or project…" required aria-required="true"
                    aria-invalid={!!errors.message} aria-describedby={errors.message ? "err-message" : undefined}
                    className={`${inputCls} resize-none`} />
                  {errors.message && <p id="err-message" className={errCls}>{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="group inline-flex min-h-[60px] w-full items-center justify-between rounded-none border border-chalk px-8 py-6 font-mono text-[12px] uppercase tracking-[0.2em] text-chalk transition-colors duration-300 hover:border-signal hover:bg-signal hover:text-ink disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
                >
                  {loading ? "Sending…" : "Send Message"}
                  {loading ? <Loader2 className="ml-4 h-4 w-4 animate-spin" /> : <ArrowRight className="ml-4 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default Contact;
